import api from './api';

const inventoryService = {
  // GRN
  getGRNs: (params) => api.get('/grn', { params }),
  getGRN: (id) => api.get(`/grn/${id}`),
  createGRN: (data) => api.post('/grn', data),
  updateGRN: (id, data) => api.put(`/grn/${id}`, data),

  // QC
  submitQC: (grnId, data) => api.post(`/grn/${grnId}/qc`, data),
  uploadQCPhotos: (grnId, formData) =>
    api.post(`/grn/${grnId}/qc/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // Stock / Inventory
  getInventory: (params) => api.get('/inventory', { params }),
  getInventoryItem: (id) => api.get(`/inventory/${id}`),
  // Phase 21 — set/move/clear the floor-plan pin on an inventory row.
  // Pass nulls to clear. zone_label is captured by the map widget so
  // the row label doesn't need a cross-service zone fetch.
  updateMapPosition: (id, { map_pin_x, map_pin_y, zone_label } = {}) =>
    api.patch(`/inventory/${id}/map-position`, {
      map_pin_x: map_pin_x == null ? null : Number(map_pin_x),
      map_pin_y: map_pin_y == null ? null : Number(map_pin_y),
      zone_label: zone_label || null,
    }),
  getMovementHistory: (params) => api.get('/inventory/movements', { params }),
  adjustStock: (data) => api.post('/inventory/adjust', data),

  /**
   * Aggregated stock summary. ``warehouse_id`` is optional — when
   * omitted the backend aggregates across every warehouse the
   * caller can see. Used by the FulfillmentCenter "stock held for
   * channel orders" widget.
   */
  getStockSummary: (params = {}) => api.get('/inventory/summary', { params }),

  // ── Damage entries (Phase 7a / 7b) ────────────────────────────
  /**
   * List damage entries. Accepts ``warehouse_id``, ``reason_code``,
   * ``approval_status``, ``inventory_id`` as optional filters —
   * matches the backend router's query params.
   */
  listDamages: (params = {}) => api.get('/inventory/damage', { params }),
  getDamage: (id) => api.get(`/inventory/damage/${id}`),

  /**
   * Phase 7b — tenant approval flow.
   * approveDamage: accept the claim; backend decrements inventory
   * and writes a DAMAGE movement. rejectDamage: reject with a
   * reason; inventory untouched. Both 409 if the entry isn't
   * PENDING (idempotency guard against double-clicks / races).
   */
  approveDamage: (id, { approvalNotes = null } = {}) => api.patch(
    `/inventory/damage/${id}/approve`,
    { approval_notes: approvalNotes },
  ),
  rejectDamage: (id, { rejectionReason }) => api.patch(
    `/inventory/damage/${id}/reject`,
    { rejection_reason: rejectionReason },
  ),

  /**
   * Phase 7c — one-shot disposition on an APPROVED damage entry.
   * ``dispositionType`` is one of:
   * DISPOSE / RETURN_TO_VENDOR / WRITE_OFF / DISCOUNT_SELL / SCRAP.
   * Backend 409s the entry isn't APPROVED or was already disposed.
   */
  disposeDamage: (id, { dispositionType, dispositionNotes = null }) => api.patch(
    `/inventory/damage/${id}/disposition`,
    { disposition_type: dispositionType, disposition_notes: dispositionNotes },
  ),

  /**
   * Aggregate damage counts for every bin in a warehouse. Used by
   * WarehouseMapTab to tint racks + BinDetailModal to show damage
   * context. One round-trip per page load vs N queries per bin.
   *
   * Response shape mirrors :class:`DamageMapResponse`:
   *   { warehouse_id, by_inventory_id: {...}, by_location: {...} }
   */
  getDamageMap: (warehouseId) => api.get('/inventory/damage/map', {
    params: { warehouse_id: warehouseId },
  }),

  /**
   * Derived stock-alerts endpoint (Phase 11b). No new tables —
   * computes low-stock / out-of-stock / overstock / expiring-soon
   * buckets on the fly from the ``inventories`` table. Callers can
   * tune the thresholds via query params; defaults match the
   * finance-sensible values (reorder 50, safety 20, overstock 500,
   * expiry window 30d).
   */
  getStockAlerts: (params = {}) => api.get('/inventory/alerts', { params }),

  // ── Zone transfers (Phase 11a) ───────────────────────────────
  /**
   * Inter-zone transfers within a single warehouse. Backend maps
   * 1:1 with :class:`Transfer` (source_zone / destination_zone +
   * line items). Note: this is NOT a cross-warehouse transfer —
   * that's a separate feature that needs its own model + endpoint.
   */
  listTransfers: (params = {}) => api.get('/inventory/transfers', { params }),
  getTransfer: (id) => api.get(`/inventory/transfers/${id}`),
  createTransfer: (body) => api.post('/inventory/transfers', body),

  // ── Cycle counts (Phase 12) ─────────────────────────────────
  /** List cycle-count sessions (tenant-scoped). Supports filters
   * for ``warehouse_id`` + ``status`` (SCHEDULED / IN_PROGRESS /
   * PAUSED / COMPLETED / CANCELLED). */
  listCycleCounts: (params = {}) => api.get('/inventory/cycle-counts', { params }),
  getCycleCount: (id) => api.get(`/inventory/cycle-counts/${id}`),
  /**
   * Create a session. ``start=true`` opens it IN_PROGRESS
   * immediately — lets the UI skip a separate "start" click for
   * SPOT counts. Inventory snapshot is server-side from the
   * zone / rack / sku filters in the body.
   */
  createCycleCount: (body, { start = false } = {}) =>
    api.post('/inventory/cycle-counts', body, { params: { start } }),
  recordCount: (sessionId, itemId, body) =>
    api.patch(`/inventory/cycle-counts/${sessionId}/items/${itemId}`, body),
  reconcileItem: (sessionId, itemId, body) =>
    api.patch(`/inventory/cycle-counts/${sessionId}/items/${itemId}/reconcile`, body),
  completeCycleCount: (sessionId) =>
    api.post(`/inventory/cycle-counts/${sessionId}/complete`),
  cancelCycleCount: (sessionId, body = {}) =>
    api.post(`/inventory/cycle-counts/${sessionId}/cancel`, body),
  getVarianceReport: (params = {}) =>
    api.get('/inventory/cycle-counts/variance', { params }),

  // ── Barcodes (Phase 13) ─────────────────────────────────────
  listLabelJobs: (params = {}) =>
    api.get('/inventory/barcodes/label-jobs', { params }),
  createLabelJob: (body) =>
    api.post('/inventory/barcodes/label-jobs', body),
  deleteLabelJob: (id) =>
    api.delete(`/inventory/barcodes/label-jobs/${id}`),
  /**
   * Stream the rendered PDF as a Blob. Returning the blob rather
   * than a static URL lets us keep ACL enforcement in-service —
   * tenants on mobile don't always have direct access to the
   * static host the ``pdf_url`` column points at.
   */
  downloadLabelJob: (id) => api.get(
    `/inventory/barcodes/label-jobs/${id}/download`,
    { responseType: 'blob' },
  ),
  listScans: (params = {}) =>
    api.get('/inventory/barcodes/scans', { params }),
  recordScan: (body) =>
    api.post('/inventory/barcodes/scans', body),

  /**
   * Finance rollup for the Damage Analytics tab. Returns counts +
   * total qty bucketed by disposition / approval / severity /
   * reason code. Warehouse id is optional — omit to aggregate
   * across everything the caller can see.
   */
  getDamageAnalytics: (warehouseId = null) => api.get('/inventory/damage/analytics', {
    params: warehouseId ? { warehouse_id: warehouseId } : {},
  }),

  /**
   * Create a damage entry with optional photo evidence. The UI
   * passes a plain object with ``photos: File[]``; we flatten into
   * multipart/form-data so each photo lands as an ``UploadFile`` on
   * the FastAPI side. Empty / falsy values are skipped so they
   * don't land as literal "null" / "undefined" strings in Form().
   */
  createDamage: async (payload, { photos = [] } = {}) => {
    const fd = new FormData();
    const set = (key, val) => {
      if (val === undefined || val === null || val === '') return;
      fd.append(key, val);
    };
    set('sku_id', payload.sku_id || payload.skuId);
    set('warehouse_id', payload.warehouse_id || payload.warehouseId);
    set('inventory_id', payload.inventory_id || payload.inventoryId);
    set('location', payload.location);
    set('qty', payload.qty);
    set('reason_code', payload.reason_code || payload.reasonCode);
    set('severity', payload.severity);
    set('description', payload.description);
    set('insurance_claim_id', payload.insurance_claim_id);
    set('damaged_by_user_id', payload.damaged_by_user_id);
    photos.forEach((file) => {
      if (file) fd.append('photos', file);
    });
    return api.post('/inventory/damage', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Dispatch
  getDispatches: (params) => api.get('/dispatch', { params }),
  getDispatch: (id) => api.get(`/dispatch/${id}`),
  createDispatch: (data) => api.post('/dispatch', data),
  updateDispatchStatus: (id, status) => api.put(`/dispatch/${id}/status`, { status }),
  // One-shot dispatch-from-bin: host picks a bin + qty on the
  // BookingDetail storage panel, backend atomically flips the
  // dispatch to DISPATCHED, decrements inventory, emits movements.
  // ``tenantId`` is a query-param override so hosts can dispatch on
  // behalf of a booking's tenant; tenants dispatching their own
  // stock can omit it (JWT scope applies).
  quickDispatch: (data, { tenantId } = {}) => api.post(
    '/dispatch/quick',
    data,
    tenantId ? { params: { tenant_id: tenantId } } : {},
  ),

  // Stats
  getInventoryStats: (params) => api.get('/inventory/stats', { params }),

  // ── Analytics ────────────────────────────────────────────────
  // ABC/XYZ inventory analysis. Real backend pulls OUTBOUND +
  // DAMAGE movements in the window × product unit cost. When no
  // window is sent the backend defaults to the trailing 365 days.
  // Returns ``{ window, warehouse_id, skus[], totals }`` — ``skus``
  // is the same shape ``classifySKUs`` consumes on the frontend.
  //
  // **Scoping (caller's responsibility):**
  //   * ``warehouseId`` set → drill-down for one warehouse.
  //   * ``warehouseIds`` set → portfolio scope across this list
  //     (use when the user picked "All warehouses" — host JWTs
  //     don't carry a ``tenant_id`` so we can't fall back to that).
  //   * Neither → backend uses the tenant_id from a tenant JWT, or
  //     returns 400 for hosts. Frontend should always pass one.
  getAbcXyzAnalytics: ({
    warehouseId = null,
    warehouseIds = null,
    fromDate = null,
    toDate = null,
  } = {}) => {
    const params = {};
    if (warehouseId) {
      params.warehouse_id = warehouseId;
    } else if (Array.isArray(warehouseIds) && warehouseIds.length > 0) {
      // Comma-separated — survives axios' default ``[]`` array
      // serialisation which FastAPI doesn't parse natively.
      params.warehouse_ids = warehouseIds.join(',');
    }
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    return api.get('/inventory/analytics/abc-xyz', { params });
  },
};

export default inventoryService;
